/**
 * De woordenoefening (stap 29), vóór het lezen: de woorden uit de tekst die
 * de beheerder voor zijn niveau goedkeurde. Backend: services/woordoefening.py.
 *
 * Passief (de standaard): hij hoort het woord ("de koffer") en ziet het staan,
 * en kiest uit vier plaatjes. Welk plaatje goed is, zegt de server pas na het
 * kiezen.
 * Actief (de leerkracht zet het aan): hij ziet het plaatje (bij een deel eerst
 * zonder, dan met), hoort de zin met een piep op de plek van het woord, en zegt
 * de hele zin. Verstaat de spraakherkenning hem niet, dan nog eens; na twee keer
 * hoort hij het woord en gaat hij door (zonder meting: kiezen uit vier zou hier
 * niets meten, hij zag het goede plaatje al).
 * Niet elk woord zegt hij: per woord ligt vast of hij het zegt of kiest
 * (item.vorm, stap 29e). Een woord dat hij kiest, gaat ook in de actieve
 * stand zoals in de passieve.
 *
 * Na elk antwoord hoort hij het goede woord, en ziet hij het in zijn moedertaal.
 * Wat fout was, komt aan het eind één keer terug; dat is oefenen, de eerste
 * keer is de meting (de leerkracht ziet hem bij Resultaten).
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Mic, Square, Volume2 } from 'lucide-react';
import { leerlingApi as api } from '../../../api/leerling';
import { Beeld } from '../../../components/Beeld';
import { Draaier, Laden } from '../../../components/Laden';
import { begin as beginOpname } from '../../../components/wavOpname';
import { stijlen } from '../stijlen';

const MAX_SECONDEN = 8;
const GAT = '___';
const beeldpad = (id) => `/student/word-image/${id}`;
const metLidwoord = (w) => (w.lidwoord ? `${w.lidwoord} ${w.woord}` : w.woord);

const LANGSTE_GELUID_MS = 10000;   // wacht hooguit zo lang op het einde van een geluid

/** Een mp3 (blob) afspelen en wachten tot hij klaar is (of mislukt, of te lang duurt). */
function speel(blob) {
  return new Promise((klaar) => {
    const wekker = setTimeout(klaar, LANGSTE_GELUID_MS);
    const af = () => { clearTimeout(wekker); klaar(); };
    try {
      if (typeof Audio === 'undefined' || !blob) { af(); return; }
      const geluid = new Audio(URL.createObjectURL(blob));
      geluid.onended = af;
      geluid.onerror = af;
      const bezig = geluid.play();
      if (bezig && bezig.catch) bezig.catch(af);
      else if (!bezig) af();   // een browser (of jsdom) zonder afspelen
    } catch (e) {
      af();
    }
  });
}

/** Een korte piep op de plek van het woord. */
function piep() {
  return new Promise((klaar) => {
    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) { klaar(); return; }
      const context = new Context();
      const toon = context.createOscillator();
      const sterkte = context.createGain();
      toon.frequency.value = 880;
      sterkte.gain.value = 0.15;
      toon.connect(sterkte).connect(context.destination);
      toon.start();
      toon.stop(context.currentTime + 0.45);
      toon.onended = () => { context.close(); klaar(); };
    } catch (e) {
      klaar();
    }
  });
}

function Zin({ zin, woord }) {
  const [voor, na] = (zin || '').split(GAT);
  return (
    <p data-testid="woord-zin" style={{ fontSize: 26, margin: '16px 0', color: 'var(--inkt)' }}>
      {voor}
      {woord ? <strong style={{ color: 'var(--goed-2)' }}>{woord}</strong>
        : <span style={{ display: 'inline-block', minWidth: 90, borderBottom: '3px solid var(--accent)' }}>&nbsp;</span>}
      {na}
    </p>
  );
}

function Terugkoppeling({ uitslag, woord, vertaling, onHoorVertaling }) {
  const goed = uitslag.goed;
  return (
    <div data-testid="terugkoppeling" style={{ marginTop: 16, padding: 16, borderRadius: 12,
      background: goed ? 'var(--goed-vlak)' : 'var(--waarschuw-vlak)' }}>
      <p style={{ fontSize: 22, margin: 0 }}>
        {goed ? (uitslag.uitkomst === 'bijna' ? 'Goed! ' : 'Goed zo! ') : uitslag.uitkomst === 'niet_verstaan' ? 'Ik kon je niet goed verstaan. ' : ''}
        Het woord is: <strong>{metLidwoord(woord)}</strong>
      </p>
      {vertaling && (
        <p style={{ fontSize: 18, margin: '8px 0 0' }} data-testid="vertaling">
          In het {vertaling.taal}: <strong dir="auto">{vertaling.vertaling}</strong>{' '}
          {vertaling.voorlezen && (
            <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px' }} onClick={onHoorVertaling}
              aria-label={`Luister in het ${vertaling.taal}`}><Volume2 size={14} /></button>
          )}
        </p>
      )}
    </div>
  );
}

export function Woorden({ les, onKlaar, onTerug, bezigMetLezen }) {
  const sessie = les.session_id;
  const [oefening, setOefening] = useState(null);
  const [fout, setFout] = useState('');
  const [rij, setRij] = useState([]);          // de woorden die nog komen: { item, ronde }
  const [terug, setTerug] = useState([]);      // fout in de eerste ronde: aan het eind nog één keer
  const [uitslag, setUitslag] = useState(null);
  const [vertaling, setVertaling] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [neemtOp, setNeemtOp] = useState(false);
  const [nogEens, setNogEens] = useState(false);   // niet verstaan: probeer het nog eens
  const [gedaan, setGedaan] = useState({ goed: 0, totaal: 0 });
  const opname = useRef(null);
  const wekker = useRef(null);
  const onder = useRef(null);   // de terugkoppeling en Volgende: in beeld na een antwoord

  useEffect(() => {   // ook als de vertaling erbij komt: die duwt de knop omlaag
    if (uitslag && onder.current && onder.current.scrollIntoView) onder.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [uitslag, vertaling]);

  useEffect(() => {
    api.haalWoorden(sessie).then((data) => {
      setOefening(data);
      setRij(data.woorden.map(item => ({ item, ronde: 1 })));
    }).catch(err => setFout(err.message));
    return () => clearTimeout(wekker.current);
  }, [sessie]);

  const huidig = rij[0];
  // zegt hij dit woord (de zin bij het plaatje), of kiest hij het uit vier?
  const zegt = (item) => (item.vorm ? item.vorm === 'spreken' : oefening?.stand === 'actief');

  const hoor = async (woord, wat) => {
    try { await speel(await api.hoorWoord(sessie, woord, wat)); } catch (e) { /* zonder geluid gaat het ook */ }
  };
  const hoorZin = async (item) => {
    const [voor, na] = (item.zin || '').split(GAT);
    if (/\w/.test(voor || '')) await hoor(item.woord, 'voor');
    await piep();
    if (/\w/.test(na || '')) await hoor(item.woord, 'na');
  };

  // een nieuw woord: bij kiezen het woord horen, bij zeggen de zin met de piep
  useEffect(() => {
    if (!huidig || !oefening) return;
    if (zegt(huidig.item)) hoorZin(huidig.item); else hoor(huidig.item.woord, 'woord');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij een nieuw woord
  }, [huidig?.item.woord, huidig?.ronde, oefening]);

  const naHetAntwoord = async (item, r) => {
    setUitslag(r);
    setNogEens(false);
    if (huidig.ronde === 1) {
      setGedaan(g => ({ goed: g.goed + (r.goed ? 1 : 0), totaal: g.totaal + 1 }));
      if (!r.goed && r.uitkomst !== 'niet_verstaan') setTerug(t => [...t, item]);
    }
    hoor(item.woord, zegt(item) ? 'zin' : 'woord');
    if (oefening.moedertaal) {
      try { setVertaling(await api.vertaalWoord(sessie, item.woord)); } catch (e) { setVertaling(null); }
    }
  };

  const kies = async (beeldId) => {
    if (uitslag || bezig) return;
    setBezig(true);
    try {
      const r = await api.kiesPlaatje(sessie, huidig.item.woord, beeldId);
      await naHetAntwoord(huidig.item, { ...r, gekozen: beeldId });
    } catch (err) {
      setFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  const stop = async () => {
    clearTimeout(wekker.current);
    const lopend = opname.current;
    opname.current = null;
    setNeemtOp(false);
    if (!lopend) return;
    setBezig(true);
    try {
      const r = await api.zegZin(sessie, huidig.item.woord, await lopend.stop());
      if (r.uitkomst === 'niet_verstaan' && r.niet_verstaan < oefening.max_niet_verstaan) {
        setNogEens(true);   // nog een keer zeggen
      } else {
        await naHetAntwoord(huidig.item, r);
      }
    } catch (err) {
      setFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  const neemOp = async () => {
    setFout('');
    try {
      opname.current = await beginOpname();
      setNeemtOp(true);
      wekker.current = setTimeout(stop, MAX_SECONDEN * 1000);
    } catch (err) {
      setFout(`De microfoon doet het niet: ${err.message}. Vraag je juf of meester om hulp.`);
    }
  };

  const volgende = () => {
    setUitslag(null);
    setVertaling(null);
    setNogEens(false);
    setRij((oud) => {
      const rest = oud.slice(1);
      if (rest.length === 0 && oud[0]?.ronde === 1 && terug.length) return terug.map(item => ({ item, ronde: 2 }));
      return rest;
    });
  };

  if (fout && !oefening) return <div className="card" style={{ maxWidth: 800, margin: '24px auto' }}><div className="alert alert-error">{fout}</div></div>;
  if (!oefening) return <Laden tekst="De woorden laden..." />;

  const kaart = { maxWidth: 820, margin: '24px auto', padding: 32, textAlign: 'center' };

  if (!oefening.beschikbaar) {
    return (
      <div className="card" style={kaart}>
        <p style={{ fontSize: 20 }}>De woorden bij deze tekst zijn nog niet klaar.</p>
        <button type="button" className="btn btn-primary mt-16" onClick={onTerug}>Terug</button>
      </div>
    );
  }

  if (!huidig) {
    return (
      <div className="card" style={kaart} data-testid="woorden-klaar">
        <h1>Klaar met de woorden!</h1>
        <p style={{ fontSize: 20, marginTop: 16 }}>
          Je hebt {oefening.woorden.length} woorden geoefend{gedaan.totaal ? `; ${gedaan.goed} wist je meteen` : ''}.
          <br />Nu ga je de tekst <strong style={{ color: 'var(--accent)' }}>{les.text_titel}</strong> lezen. Let op de woorden!
        </p>
        <button type="button" className="btn btn-primary mt-24" style={{ padding: '16px 32px', fontSize: 18 }}
          onClick={onKlaar} disabled={bezigMetLezen}>
          {bezigMetLezen ? <Draaier grootte={20} /> : <>Ga lezen <ChevronRight size={20} /></>}
        </button>
      </div>
    );
  }

  const { item, ronde } = huidig;
  const totaal = oefening.woorden.length;
  const plek = ronde === 1 ? totaal - rij.length + 1 : null;

  return (
    <div className="card" style={kaart}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button type="button" className="btn btn-secondary" style={{ padding: '6px 12px' }} onClick={onTerug}>Terug</button>
        <span className="text-muted" data-testid="woord-teller">
          {ronde === 1 ? `Woord ${plek} van ${totaal}` : 'Nog één keer'}
        </span>
      </div>
      {fout && <div className="alert alert-error mt-16">{fout}</div>}

      {!zegt(item) ? (
        <>
          <div style={{ background: stijlen.annaMariaVerloop, borderRadius: 12, padding: 16, marginTop: 16 }}>
            <p style={{ fontSize: 20, margin: 0 }}>Welk plaatje is</p>
            <p style={{ fontSize: 34, margin: '4px 0', color: 'var(--inkt)' }}>
              <strong data-testid="het-woord">{metLidwoord(item)}</strong>{' '}
              <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => hoor(item.woord, 'woord')}
                aria-label="Luister nog eens"><Volume2 size={18} /></button>
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 190px))', justifyContent: 'center', gap: 16, marginTop: 20 }}>
            {item.opties.map((id, i) => {
              const isJuist = uitslag && id === uitslag.juist;
              const isFoutGekozen = uitslag && id === uitslag.gekozen && !uitslag.goed;
              return (
                <button key={id} type="button" onClick={() => kies(id)} disabled={!!uitslag || bezig}
                  aria-label={`Plaatje ${i + 1}`} data-testid="plaatje"
                  style={{ padding: 6, borderRadius: 12, cursor: uitslag ? 'default' : 'pointer', background: 'var(--wit)',
                    border: `4px solid ${isJuist ? 'var(--goed-2)' : isFoutGekozen ? 'var(--fout)' : 'var(--lijn)'}`,
                    opacity: uitslag && !isJuist && !isFoutGekozen ? 0.45 : 1 }}>
                  <Beeld pad={beeldpad(id)} alt="" grootte={170} />
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 16 }}>
            {item.paar && item.basis && (
              <>
                <Beeld pad={beeldpad(item.basis)} alt="eerst" grootte={220} style={{ border: '1px solid var(--lijn)' }} />
                <ChevronRight size={32} color="var(--stil)" />
              </>
            )}
            <Beeld pad={beeldpad(item.beeld)} alt="" grootte={item.paar ? 220 : 280} style={{ border: '1px solid var(--lijn)' }} />
          </div>
          <Zin zin={item.zin} woord={uitslag ? item.woord : null} />
          {!uitslag && (
            <>
              <p className="text-muted">Zeg de hele zin, met het woord op de plek van de piep.</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 12 }}>
                <button type="button" className="btn btn-secondary" onClick={() => hoorZin(item)} disabled={neemtOp || bezig}>
                  <Volume2 size={18} /> Luister
                </button>
                {neemtOp ? (
                  <button type="button" className="btn btn-danger" onClick={stop} style={{ padding: '12px 24px', fontSize: 18 }}>
                    <Square size={18} /> Klaar
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={neemOp} disabled={bezig} style={{ padding: '12px 24px', fontSize: 18 }}>
                    {bezig ? <><Draaier grootte={18} /> Ik luister...</> : <><Mic size={18} /> Zeg de zin</>}
                  </button>
                )}
              </div>
              {neemtOp && <p className="text-muted mt-8">Zeg nu de zin. Klik op Klaar als je klaar bent.</p>}
              {nogEens && <p className="mt-8" style={{ fontSize: 18, color: 'var(--waarschuw-tekst)' }} data-testid="nog-eens">
                Ik verstond je niet goed. Probeer het nog eens, iets langzamer en dichter bij de microfoon.</p>}
            </>
          )}
        </>
      )}

      {uitslag && (
        <>
          <Terugkoppeling uitslag={uitslag} woord={item} vertaling={vertaling}
            onHoorVertaling={() => hoor(item.woord, 'vertaling')} />
          <button ref={onder} type="button" className="btn btn-primary mt-16" style={{ padding: '12px 28px', fontSize: 18 }} onClick={volgende}>
            Volgende <ChevronRight size={18} />
          </button>
        </>
      )}
    </div>
  );
}
